import type React from "react";
import { useCallback, useMemo, type KeyboardEvent, useState } from "react";

import isHotkey from "is-hotkey";
import {
  Bold,
  Italic,
  Underline,
  Code,
  Quote,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  ChevronDown,
  Indent,
  Outdent,
  PanelBottom,
  Plus,
} from "lucide-react";
import {
  Button,
  ButtonGroup,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import {
  createEditor,
  type Descendant,
  Editor,
  Element as SlateElement,
  Transforms,
  Node,
} from "slate";
import { withHistory } from "slate-history";
import {
  Slate,
  Editable,
  withReact,
  useSlate,
  type RenderElementProps,
  type RenderLeafProps,
  ReactEditor,
} from "slate-react";

import { CustomCommands } from "./editorCommands";
import type { CustomElementFormat, CustomTextKey } from "./types";

// Re-export types
export type { Descendant } from "slate";
export type { CustomElement } from "./types";

export interface RichTextEditorProps
  extends Omit<React.ComponentProps<typeof Editable>, "onChange" | "value"> {
  value: Descendant[];
  onChange: (value: Descendant[]) => void;
  readOnly?: boolean;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
}

const HOTKEYS: Record<string, CustomTextKey> = {
  "mod+b": "bold",
  "mod+i": "italic",
  "mod+u": "underline",
  "mod+`": "code",
};

const BLOCK_TYPES = [
  { value: "paragraph", label: "Normal" },
  { value: "heading-one", label: "Heading 1" },
  { value: "heading-two", label: "Heading 2" },
] as const;

const ALIGNMENT_TYPES = ["left", "center", "right", "justify"] as const;

const Element = ({ attributes, children, element }: RenderElementProps) => {
  const style: React.CSSProperties = {};
  if (
    "align" in element
    && element.align
    && ALIGNMENT_TYPES.includes(element.align as any)
  ) {
    style.textAlign = element.align as React.CSSProperties["textAlign"];
  }
  if ("indent" in element && typeof element.indent === "number") {
    style.marginLeft = `${element.indent * 2}em`;
  }

  switch (element.type) {
    case "block-quote":
      return (
        <blockquote className="blockquote" style={style} {...attributes}>
          {children}
        </blockquote>
      );
    case "bulleted-list":
      return (
        <ul style={style} {...attributes}>
          {children}
        </ul>
      );
    case "heading-one":
      return (
        <h1 style={style} {...attributes}>
          {children}
        </h1>
      );
    case "heading-two":
      return (
        <h2 style={style} {...attributes}>
          {children}
        </h2>
      );
    case "list-item":
      return (
        <li style={style} {...attributes}>
          {children}
        </li>
      );
    case "numbered-list":
      return (
        <ol style={style} {...attributes}>
          {children}
        </ol>
      );
    // TODO #campaignLandingPage - switch to using reactstrap uncontroller accordions when rendering in presentation mode. Will need to track open state of each item, or use UncontrolledAccordion from reactstrap. Also need toggle func.
    case "accordion":
      return (
        <div {...attributes} style={style} className="accordion">
          {children}
        </div>
      );
    case "accordion-item": {
      return (
        <div {...attributes} style={style} className="accordion-item">
          {children}
        </div>
      );
    }
    // TODO #campaignLandingPage - add targetId attr to accordion item headers. Corresponds to accordionId of accordion item body.
    case "accordion-item-header":
      return (
        <h2 {...attributes} style={style} className="accordion-header">
          <span className="accordion-button collapsed">{children}</span>
        </h2>
      );
    // TODO #campaignLandingPage - add accordionId attr to accordion item headers. Corresponds to targetId of accordion item body.
    case "accordion-item-body":
      return (
        <div
          {...attributes}
          style={style}
          className="accordion-collapse collapse show"
        >
          <div className="accordion-body">{children}</div>
        </div>
      );
    // TODO #campaignLanding - add elements for media (images primarily) and links (review additional security considerations for links)

    default:
      return (
        <p
          className="mb-2"
          style={{ maxWidth: "100%", ...style }}
          {...attributes}
        >
          {children}
        </p>
      );
  }
};

const Leaf = ({ attributes, children, leaf }: RenderLeafProps) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }

  if (leaf.code) {
    children = <code>{children}</code>;
  }

  if (leaf.italic) {
    children = <em>{children}</em>;
  }

  if (leaf.underline) {
    children = <u>{children}</u>;
  }

  return <span {...attributes}>{children}</span>;
};

interface BlockButtonProps {
  format: CustomElementFormat;
  icon: React.ElementType | React.FC;
  title?: string;
  onCustomClick?: (event: React.MouseEvent) => void;
}

const BlockButton = ({
  format,
  icon: Icon,
  title,
  onCustomClick,
}: BlockButtonProps) => {
  const editor = useSlate();
  const isAlignFormat = ["left", "center", "right", "justify"].includes(format);

  return (
    <Button
      color="light"
      size="sm"
      title={title}
      type="button"
      active={CustomCommands.isBlockActive(
        editor,
        format,
        isAlignFormat ? "align" : "type",
      )}
      onMouseDown={
        onCustomClick
        || ((event) => {
          event.preventDefault();
          CustomCommands.toggleBlock(editor, format);
        })
      }
    >
      {typeof Icon === "function" ? <Icon /> : <Icon size={16} />}
    </Button>
  );
};

interface MarkButtonProps {
  format: CustomTextKey;
  icon: React.ElementType;
  title?: string;
}

const MarkButton = ({ format, icon: Icon, title }: MarkButtonProps) => {
  const editor = useSlate();
  return (
    <Button
      color="light"
      size="sm"
      title={title}
      type="button"
      active={CustomCommands.isMarkActive(editor, format)}
      onMouseDown={(event) => {
        event.preventDefault();
        CustomCommands.toggleMark(editor, format);
      }}
    >
      <Icon size={16} />
    </Button>
  );
};

const AccordionButton = () => {
  const editor = useSlate();

  // Check if we're inside an accordion to show appropriate button text
  const isInsideAccordion = useMemo(() => {
    const { selection } = editor;
    if (!selection) return false;

    const [accordionMatch] = Array.from(
      Editor.nodes(editor, {
        at: selection,
        match: (n) =>
          !Editor.isEditor(n)
          && SlateElement.isElement(n)
          && n.type === "accordion",
      }),
    );

    return !!accordionMatch;
  }, [editor]);

  // Custom icon component that conditionally renders the plus sign
  const AccordionIcon = () => (
    <>
      <PanelBottom size={16} />
      {isInsideAccordion && <Plus size={12} style={{ marginLeft: "2px" }} />}
    </>
  );

  return (
    <BlockButton
      format="accordion"
      icon={AccordionIcon}
      title={isInsideAccordion ? "Add Accordion Item" : "Add Accordion"}
      onCustomClick={(event) => {
        event.preventDefault();
        CustomCommands.insertAccordionOrItem(editor);
      }}
    />
  );
};

const BlockTypeDropdown = () => {
  const editor = useSlate();
  const [isOpen, setIsOpen] = useState(false);

  // Get current block type - use editor.children to detect content changes
  const currentBlockType = useMemo(() => {
    const { selection } = editor;
    if (!selection) return BLOCK_TYPES[0];

    const blockType = BLOCK_TYPES.find((type) =>
      CustomCommands.isBlockActive(
        editor,
        type.value as CustomElementFormat,
        "type",
      ),
    );
    return blockType || BLOCK_TYPES[0]; // Default to 'Normal'
  }, [editor]);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleSelect = useCallback(
    (format: CustomElementFormat, event: React.MouseEvent) => {
      event.preventDefault();
      CustomCommands.toggleBlock(editor, format);
      setIsOpen(false);
      // Ensure editor stays focused
      ReactEditor.focus(editor);
    },
    [editor],
  );

  return (
    <Dropdown isOpen={isOpen} toggle={handleToggle}>
      <DropdownToggle
        color="light"
        size="sm"
        caret={false}
        type="button"
        style={{ minWidth: "120px", textAlign: "left" }}
      >
        {currentBlockType.label}
        <ChevronDown size={14} className="ms-2 float-end mt-1" />
      </DropdownToggle>
      <DropdownMenu>
        {BLOCK_TYPES.map((blockType) => (
          <DropdownItem
            key={blockType.value}
            active={currentBlockType.value === blockType.value}
            onMouseDown={(event) =>
              handleSelect(blockType.value as CustomElementFormat, event)
            }
          >
            {blockType.label}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
};

const Toolbar = () => {
  const editor = useSlate();

  return (
    <div className="border-bottom p-2 mb-0 d-flex align-items-center">
      <ButtonGroup size="sm">
        <MarkButton format="bold" icon={Bold} title="Bold (Ctrl+B)" />
        <MarkButton format="italic" icon={Italic} title="Italic (Ctrl+I)" />
        <MarkButton
          format="underline"
          icon={Underline}
          title="Underline (Ctrl+U)"
        />
        <MarkButton format="code" icon={Code} title="Code (Ctrl+`)" />
      </ButtonGroup>
      <ButtonGroup size="sm" className="m-1">
        <BlockTypeDropdown />
      </ButtonGroup>

      <ButtonGroup size="sm" className="m-1">
        <BlockButton format="left" icon={AlignLeft} title="Align Left" />
        <BlockButton format="center" icon={AlignCenter} title="Align Center" />
        <BlockButton format="right" icon={AlignRight} title="Align Right" />
        <BlockButton format="justify" icon={AlignJustify} title="Justify" />
      </ButtonGroup>
      <ButtonGroup size="sm" className="m-1">
        <BlockButton format="block-quote" icon={Quote} title="Block Quote" />
        <BlockButton
          format="numbered-list"
          icon={ListOrdered}
          title="Numbered List"
        />
        <BlockButton format="bulleted-list" icon={List} title="Bulleted List" />
      </ButtonGroup>
      <ButtonGroup size="sm" className="m-1">
        <Button
          color="light"
          size="sm"
          title="Decrease Indent (Shift+Tab)"
          type="button"
          onMouseDown={(event) => {
            event.preventDefault();
            CustomCommands.decreaseIndent(editor);
          }}
        >
          <Outdent size={16} />
        </Button>
        <Button
          color="light"
          size="sm"
          title="Increase Indent (Tab)"
          type="button"
          onMouseDown={(event) => {
            event.preventDefault();
            CustomCommands.increaseIndent(editor);
          }}
        >
          <Indent size={16} />
        </Button>
      </ButtonGroup>
      <ButtonGroup size="sm" className="m-1">
        <AccordionButton />
      </ButtonGroup>
    </div>
  );
};

// Normalization plugin for accordion structure
const withAccordionNormalization = (editor: Editor) => {
  const { normalizeNode } = editor;

  editor.normalizeNode = ([node, path]) => {
    if (SlateElement.isElement(node)) {
      // Prevent nested accordions
      if (
        node.type === "accordion-item-body"
        || node.type === "accordion-item-header"
      ) {
        for (const [child, childPath] of Node.children(editor, path)) {
          if (SlateElement.isElement(child) && child.type === "accordion") {
            // Remove nested accordions only
            Transforms.removeNodes(editor, { at: childPath });
            return;
          }
        }
      }

      // Ensure accordion-item-header only contains text nodes (single line)
      if (node.type === "accordion-item-header") {
        for (const [child, childPath] of Node.children(editor, path)) {
          if (SlateElement.isElement(child)) {
            // Unwrap any block elements and convert to text
            Transforms.unwrapNodes(editor, { at: childPath });
            return;
          }
        }
      }
    }

    // Ensure editor always ends with an empty paragraph
    if (Editor.isEditor(node)) {
      const children = Array.from(Node.children(editor, path));

      if (children.length === 0) {
        // If editor is completely empty, add a paragraph
        Transforms.insertNodes(
          editor,
          { type: "paragraph", children: [{ text: "" }] },
          { at: [0] },
        );
        return;
      }

      const [lastChild] = children[children.length - 1];

      // Check if last element is an empty paragraph
      const isLastElementEmptyParagraph
        = SlateElement.isElement(lastChild)
        && lastChild.type === "paragraph"
        && lastChild.children.length === 1
        && lastChild.children[0].text === "";

      if (!isLastElementEmptyParagraph) {
        // Add empty paragraph at the end
        Transforms.insertNodes(
          editor,
          { type: "paragraph", children: [{ text: "" }] },
          { at: [children.length] },
        );
        return;
      }
    }

    // Call the original normalizeNode for other rules
    normalizeNode([node, path]);
  };

  return editor;
};

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder,
  readOnly,
  className,
  style,
  ...props
}) => {
  const renderElement = useCallback(
    (props: RenderElementProps) => <Element {...props} />,
    [],
  );
  const renderLeaf = useCallback(
    (props: RenderLeafProps) => <Leaf {...props} />,
    [],
  );

  const editor = useMemo(
    () => withAccordionNormalization(withHistory(withReact(createEditor()))),
    [],
  );

  const handleChange = useCallback(
    (newValue: Descendant[]) => {
      onChange(newValue);
    },
    [onChange],
  );

  return (
    <Slate editor={editor} initialValue={value} onChange={handleChange}>
      {!readOnly && <Toolbar />}
      <div
        className="editor-container"
        style={{
          position: "relative",
          flex: 1,
          overflow: "auto",
        }}
      >
        <Editable
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          placeholder={placeholder}
          readOnly={readOnly}
          className={className}
          style={{
            height: "100%",
            minHeight: "1rem",
            padding: "1rem",
            outline: "none",
            overflowY: "auto",
            ...style,
          }}
          onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
            // Check if we're inside an accordion header
            const { selection } = editor;
            const isInAccordionHeader
              = selection
              && Editor.above(editor, {
                match: (n) =>
                  !Editor.isEditor(n)
                  && SlateElement.isElement(n)
                  && n.type === "accordion-item-header",
              });

            // Prevent Enter key in accordion headers (single line only)
            if (event.key === "Enter" && isInAccordionHeader) {
              event.preventDefault();
              return;
            }

            // Handle hotkeys
            for (const hotkey in HOTKEYS) {
              if (isHotkey(hotkey, event as any)) {
                event.preventDefault();
                const mark = HOTKEYS[hotkey];
                CustomCommands.toggleMark(editor, mark);
                return;
              }
            }

            // Handle Tab for indentation
            if (event.key === "Tab") {
              // Prevent both tab character insertion and focus change
              event.preventDefault();

              if (event.shiftKey) {
                CustomCommands.decreaseIndent(editor);
              } else {
                CustomCommands.increaseIndent(editor);
              }
              return;
            }

            // Handle Escape to blur editor
            if (event.key === "Escape") {
              event.preventDefault();
              (event.target as HTMLElement).blur();
            }
          }}
          {...props}
        />
      </div>
    </Slate>
  );
};
